import { Context, Controller } from '@hotwired/stimulus';
import * as d3 from 'd3';
import { emitter } from 'src/emitter';

export default class GraphsCalendarController extends Controller {
  public static targets = ['d3', 'table'];

  declare private hasTableTarget: boolean;
  declare private hasD3Target: boolean;
  declare private tableTarget: HTMLTableElement;
  declare private d3Target: SVGSVGElement;

  public static values = {
    data: Object,
    valueScale: Number,
    colorClasses: Array,
    foregroundFillClass: String,
    backgroundFillClass: String,
    indicatorFillClass: String,
    highlightFill: String,
    interactable: Boolean,
    selected: Boolean,
    identifier: String,
  };

  declare private readonly dataValue: Readonly<{
    readonly data: readonly Readonly<{
      date: string;
      data: Readonly<{
        readonly full: number;
        readonly partial: number;
        readonly empty: number;
        readonly open_actions?: number;
        readonly closed_actions?: number;
      }>;
    }>[];
    readonly date_start: string;
    readonly date_end: string;
    readonly scope_year: number | null;
  }>;

  declare private readonly colorClassesValue: string[];
  declare private readonly foregroundFillClassValue: string;
  declare private readonly backgroundFillClassValue: string;
  declare private readonly indicatorFillClassValue: string;
  declare private readonly highlightFillValue: string;
  declare private readonly identifierValue: string;
  private focused: boolean;

  constructor(props: Context) {
    super(props);

    this.focused = false;

    this.onFocused = this.onFocused.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.onClick = this.onClick.bind(this);
  }

  connect(): void {
    const cellSize = 20; // height of a day
    const cellPadding = 1;
    const height = cellSize * 9; // height of a week (5 days + padding)
    const width = 41 + cellSize * 53; // width of the chart

    // Define formatting functions for the axes and tooltips.
    const formatDate = (date: Date) =>
      date.toLocaleDateString(navigator.language || 'en-GB');
    const formatDay = (i: number) =>
      ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][i];
    const formatMonth = d3.utcFormat('%b');

    // End values are exclusive, last values are inclusive
    const lastDay = d3.utcDay(new Date(this.dataValue.date_end));
    const endDate = d3.timeDay.offset(lastDay, 1);
    const lastMonth = d3.utcMonth(lastDay);
    const endMonth = d3.timeMonth.offset(lastMonth, 1);
    const oneYearAgo = d3.utcDay(d3.timeYear.offset(endDate, -1));

    // Get the correct dates, ensuring the first month displayed is equal to or
    // later than the first date displayed:"
    //
    // - If the start date is the first of the month, it is used, otherwise, the
    //   next month is used so we don't need to figure out the placement of the
    //   first month label.
    // - If the start date's first day of the week falls in the same week as the
    //   first day of the week of the start month, the real start date is used.
    //   Otherwise, we skip a few days. Again, this is to simplify the labels.
    // - Finally, the given start date is used if it's less than a year ago
    //   ensuring we show at most one year of data.
    const minimumStartDate =
      new Date(this.dataValue.date_start) > oneYearAgo
        ? d3.utcMonth(new Date(this.dataValue.date_start))
        : oneYearAgo;
    const startMonth =
      d3.utcDay(d3.utcMonth(minimumStartDate)).getTime() ===
      d3.utcDay(minimumStartDate).getTime()
        ? minimumStartDate
        : d3.timeMonth.offset(d3.utcMonth(minimumStartDate), 1);
    const startWeek = d3.utcWeek(startMonth);
    const startDate =
      startWeek.getTime() === d3.utcWeek(minimumStartDate).getTime()
        ? minimumStartDate
        : d3.utcDay(startWeek);
    const startYear = d3.utcYear(startMonth).getUTCFullYear();

    const months = d3.utcMonths(startMonth, endMonth);
    const dates = d3.utcDays(startDate, endDate);

    // Helpers to compute a day’s position in the week.
    const timeWeek = d3.utcSunday;

    // Compute the values used to color the cells: percent change is the difference between the day’s
    // closing value and the previous day’s, as a fraction of the latter.
    const submissions = this.dataValue.data
      .map(({ data, date }) => ({
        value:
          data.full > 0
            ? data.full
            : data.partial > 0
              ? data.partial
              : data.empty,
        isPartial: data.full === 0 && data.partial > 0,
        hasEmpty: data.full === 0 && data.partial === 0 && data.empty > 0,
        isEmpty: false,
        date: new Date(date),
        hasOpenTasks:
          typeof data.open_actions === 'number' && data.open_actions > 0,
        tasks: (data.closed_actions ?? 0) + (data.open_actions ?? 0),
      }))
      .reduce<
        Record<
          number,
          {
            value: number;
            isPartial: boolean;
            isEmpty: boolean;
            hasEmpty: boolean;
            hasOpenTasks: boolean;
            tasks: number;
            date: Date;
          }
        >
      >((result, datum) => {
        result[d3.utcDay(datum.date).getTime()] = datum;
        return result;
      }, {});

    type Datum = {
      index: number;
      value: number;
      isPartial: boolean;
      hasEmpty: boolean;
      isEmpty: boolean;
      hasOpenTasks: boolean;
      tasks: number;
      date: Date;
      x: number;
      y: number;
    };

    const data: Array<Datum> = dates.map((date, i) => {
      const datum = submissions[date.getTime()] ?? {
        date,
        value: 0,
        isPartial: false,
        hasEmpty: false,
        isEmpty: true,
        hasOpenTasks: false,
        tasks: 0,
      };

      const x =
        timeWeek.count(startDate, datum.date) * cellSize + cellPadding / 2;
      const y = datum.date.getUTCDay() * cellSize + cellPadding / 2;
      return { ...datum, index: i, x, y };
    });

    console.log({ data });

    // Compute the extent of the value, ignore the outliers and define a diverging and symmetric color scale.
    const max =
      d3.quantile(
        Object.values(submissions).filter((a) => a.value !== 0),
        0.9975,
        (d) => Math.abs(d.value),
      ) || 0;
    const min = 0;

    let [colorMin, ...colorBuckets] = this.colorClassesValue;
    const colorMax = colorBuckets.pop()!;
    const highlight = (_: number) => this.highlightFillValue;
    /*d3
      .scaleSequential(d3.interpolateRdYlGn)
      .domain([min + 1, max]);*/

    const color = (value: number): string => {
      if (value <= min) {
        return colorMin;
      }

      if (value >= max) {
        return colorMax;
      }

      return colorBuckets[Math.floor((value / max) * colorBuckets.length)];
    };

    // Group data by year, in reverse input order. (Since the dataset is chronological,
    // this will show years in reverse chronological order.)
    const years = d3.groups(data, () => startYear); // d3.groups(data, (d) => d.date.getUTCFullYear()).reverse();
    // const startWeek = d3.utcWeek(new Date(this.dataValue.date_start));

    // A function that draws a thin white line to the left of each month.
    function pathMonth(t: Date) {
      // Get the week-of-the-day from the date (should always be 0-6)
      const d = Math.max(0, Math.min(6, t.getUTCDay()));

      // Count the weeks from the first displayed date to this one
      // timeWeek.count(timeWeek.floor(startDate), timeWeek.ceil(t));
      // timeWeek.count(d3.utcYear(t), t);
      const w = timeWeek.count(startDate, timeWeek.floor(t));

      // Finally, draw a line from top to bottom, where the indent is on the/ weekday the month starts. If sunday is the
      // first day of the month, it's a special case.
      return `${
        d === 0 // Sunday is top of the graph
          ? `M${w * cellSize},0`
          : d === 1
            ? `M${(w + 1) * cellSize},0`
            : `M${(w + 1) * cellSize},0V${d * cellSize}H${w * cellSize}`
      }V${7 * cellSize}`;
    }

    const svg = d3
      .create('svg')
      .attr('data-graphs--calendar-target', 'd3')
      .attr('aria-hidden', 'true')
      .attr('width', width)
      .attr('height', height * years.length)
      .attr('viewBox', [0, 0, width, height * years.length])
      .attr('style', 'font: 11px sans-serif;')
      .classed('print:max-w-full', true);

    const year = svg
      .selectAll('g')
      .data(years)
      .join('g')
      .attr(
        'transform',
        (_, i) => `translate(40.5,${height * i + cellSize * 1.5})`,
      );

    year
      .append('text')
      .classed('fill-transparent', true)
      .attr('x', -5)
      .attr('y', -5)
      .attr('font-weight', 'bold')
      .attr('text-anchor', 'end')
      .text(([key]) => key);

    year
      .append('g')
      .attr('data-graph-group', 'axis-left')
      .attr('text-anchor', 'end')
      .classed('fill-current', true)
      .selectAll()
      .data(d3.range(0, 7))
      .join('text')
      .attr('x', -5)
      .attr('y', (i) => (i + 0.5) * cellSize)
      .attr('dy', '0.31em')
      .text(formatDay);

    // d3.utcDay(d.date).getUTCDate() === 1 ? 'text-red-500'
    const cells = year
      .append('g')
      .attr('data-graph-group', 'data')
      .attr('fill', 'currentColor')
      .selectAll()
      .data(([, values]) => values)
      .join('g')
      .attr('id', (d) => `${this.identifierValue}-cells-${d.index}`)
      .attr('data-ref', (d) => d.date.toISOString().split('T').shift()!)
      .attr('transform', (d) => `translate(${d.x}, ${d.y})`)
      .attr('pointer-events', 'none');

    const colorIndicatorClassName = this.indicatorFillClassValue;

    cells.each(function (datum, _index, _groups) {
      if (!this) {
        return;
      }

      /*.attr(
        'x',
        (d) => timeWeek.count(startDate, d.date) * cellSize + cellPadding / 2,
      )
      .attr('y', (d) => d.date.getUTCDay() * cellSize + cellPadding / 2)*/

      const cell = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'rect',
      );
      cell.setAttribute('class', color(datum.value));
      cell.setAttribute('width', String(cellSize - cellPadding));
      cell.setAttribute('height', String(cellSize - cellPadding));
      cell.setAttribute('pointer-events', 'all');

      /*
      const title = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'title',
      );
      title.textContent = `${formatDate(datum.date)}
${datum.value} entries ${datum.isPartial ? ' (partial)' : ''}`;

      cell.append(title); */
      this.append(cell);

      if (datum.tasks === 0) {
        if (datum.hasEmpty) {
          const indicator = document.createElementNS(
            'http://www.w3.org/2000/svg',
            'circle',
          );

          indicator.setAttribute('cx', String((cellSize - cellPadding) / 2));
          indicator.setAttribute('cy', String((cellSize - cellPadding) / 2));
          indicator.setAttribute('r', String(cellSize / 4 - 1));
          indicator.setAttribute('class', colorIndicatorClassName);
          indicator.setAttribute('fill', 'transparent');
          indicator.setAttribute('stroke', 'currentColor');
          indicator.setAttribute('stroke-width', String(2));
          indicator.setAttribute('stroke-dasharray', '1.5');
          indicator.setAttribute('pointer-events', 'none');

          this.append(indicator);
        } else {
          return;
        }
      } else {
        const indicator = document.createElementNS(
          'http://www.w3.org/2000/svg',
          'circle',
        );

        const hasOpenTasks = datum.hasOpenTasks;

        indicator.setAttribute('cx', String((cellSize - cellPadding) / 2));
        indicator.setAttribute('cy', String((cellSize - cellPadding) / 2));
        indicator.setAttribute(
          'r',
          String(cellSize / 4 - (hasOpenTasks ? 1 : 0)),
        );
        indicator.setAttribute('class', colorIndicatorClassName);
        indicator.setAttribute(
          'fill',
          hasOpenTasks ? 'transparent' : 'currentColor',
        );
        indicator.setAttribute('stroke', 'currentColor');
        indicator.setAttribute('stroke-width', String(hasOpenTasks ? 2 : 0));
        indicator.setAttribute('pointer-events', 'none');

        this.append(indicator);
      }
    });

    cells
      .on('pointerenter pointermove', onPointerMoved)
      .on('pointerleave', onPointerLeft)
      .on('click', onClick);
    // .on('touchstart', (event) => event.preventDefault());

    const month = year
      .append('g')
      .attr('pointer-events', 'none')
      .attr('data-graph-group', 'months')
      .selectAll()
      .data(months)
      .join('g')
      .attr('data-graph-group', 'month')
      .attr('data-ref', (d) => d.getUTCMonth());

    month
      .filter((_, i) => !!i)
      .append('path')
      .classed(this.backgroundFillClassValue, true)
      .attr('fill', 'none')
      .attr('stroke', 'currentColor')
      .attr('stroke-width', 3)
      .attr('d', pathMonth);

    month
      .append('text')
      .attr('fill', 'currentColor')
      .attr(
        'x',
        (d) => timeWeek.count(startDate, timeWeek.ceil(d)) * cellSize + 2,
      )
      .attr('y', -5)
      .text(formatMonth);

    const tooltip = svg
      .append('g')
      .attr('id', `tooltip-${this.identifierValue}`)
      .attr('pointer-events', 'none')
      .style('z-index', 1);

    const activePointers: Record<number, true> = {};
    function onPointerMoved(event: PointerEvent, d: Datum) {
      activePointers[event.pointerId] = true;

      if (event.isPrimary) {
        svg.classed('--pointed', true);

        highlightDatum(d);
      }
    }

    function onPointerLeft(event: PointerEvent) {
      delete activePointers[event.pointerId];

      if (event.isPrimary) {
        svg.classed('--pointed', false);

        // Hide tooltip
        tooltip.style('display', 'none').attr('data-index', null);

        // Remove highlight references
        const svgElement = svg.node()!;
        const currentRef = svgElement.getAttribute('data-highlight');
        const currentRect = svgElement.querySelector(
          `g[data-ref="${currentRef}"] > rect`,
        );

        svgElement.removeAttribute('data-highlight');
        svgElement.classList.remove('cursor-pointer');

        // Remove highlight from rect
        if (currentRect) {
          currentRect.removeAttribute('fill');
        }
      }
    }

    function onClick(_: PointerEvent, d: Datum) {
      selectDatum(d);
    }

    function highlightDatum(d: Datum) {
      const svgElement = svg.node()!;
      const currentRef = svgElement.getAttribute('data-highlight');
      const currentRect = svgElement.querySelector(
        `g[data-ref="${currentRef}"] > rect`,
      );
      const nextRef = String(d.date.toISOString().split('T').shift());
      const nextRect = svgElement.querySelector(
        `g[data-ref="${nextRef}"] > rect`,
      );

      if (currentRect) {
        // Remove highlight from rect
        if (d.isEmpty || currentRef !== nextRef) {
          currentRect.removeAttribute('fill');
        }
      }

      if (d.isEmpty) {
        // Hide tooltip
        tooltip.style('display', 'none').attr('data-ref', null);

        // Remove highlight references
        svgElement.removeAttribute('data-highlight');
        svgElement.classList.remove('cursor-pointer');
      } else if (currentRef !== nextRef) {
        // Highlight the new one
        svgElement.setAttribute('data-highlight', nextRef);
        svgElement.classList.add('cursor-pointer');

        // Add highlight to rect
        if (nextRect) {
          nextRect.setAttribute('fill', highlight(d.value));
        }

        tooltip.style('display', null).attr('data-ref', nextRef);

        const bubble = tooltip
          .selectAll('rect')
          .data([undefined, undefined])
          .join('rect')
          .attr('fill', 'rgba(255, 255, 255, .7)')
          .attr('stroke', 'rgba(0, 0, 0, .7)')
          .attr('stroke-width', '1');

        const text = tooltip
          .selectAll('text')
          .data([undefined, undefined])
          .join('text')
          .call((text) =>
            text
              .selectAll('tspan')
              .data(
                [
                  formatDate(d.date),
                  `Submissions: ${d.value} ${d.isPartial ? '(partial)' : d.hasEmpty ? '(empty)' : ''}`,
                  d.tasks > 0
                    ? `Actions: ${d.tasks} ${d.hasOpenTasks ? ' with at least 1 still open' : 'resolved'}`
                    : null,
                ].filter(Boolean),
              )
              .join('tspan')
              .attr('x', 0)
              .attr('y', (_, i) => `${i * 1.1}em`)
              .attr('font-size', 11)
              .attr('font-weight', (_, i) => (i ? null : 'bold'))
              .text((d) => d),
          );

        const { width: w, height: h } = (
          text.node()! as SVGTextElement
        ).getBBox();

        const boxPadding = 5;

        // Set bubble
        bubble
          .attr('x', -boxPadding)
          .attr('y', -boxPadding)
          .attr('width', w + boxPadding * 2)
          .attr('height', h + boxPadding * 2);

        text.attr('transform', `translate(0,10)`);

        // Ensure stays in bound
        const left =
          40.5 +
          Math.min(
            Math.max(w + boxPadding * 2, d.x - boxPadding * 2),
            width - boxPadding * 2,
          );

        const top =
          height * 0 +
          cellSize * 1.5 +
          Math.min(
            Math.max(boxPadding * 2, d.y - boxPadding * 2),
            height - boxPadding * 2,
          );

        tooltip.attr('transform', `translate(${left - w},${top - h})`);
      }
    }

    function selectDatum(d?: Datum) {
      if (!d || d.isEmpty) {
        emitter.emit('cadence-grid:deselect', {});
        return;
      }

      const activeDateIso8601 = d.date.toISOString().split('T').shift() || '';
      emitter.emit('cadence-grid:select', { iso8601: activeDateIso8601 });
    }

    function getSelectedIndex() {
      const selectedId = svg.node()?.getAttribute('aria-activedescendant');
      if (selectedId) {
        if (cells.select(selectedId)) {
          const index = cells
            .nodes()
            .findIndex((potential) => potential!.id === selectedId);

          if (index !== -1) {
            return index;
          }
        }
      }

      const selectedRef = new Date().toISOString().split('T').shift();
      const selectedGroup = cells.select(`g[data-ref="${selectedRef}"]`);

      if (selectedGroup) {
        const index = cells
          .nodes()
          .findIndex(
            (potential) => potential!.getAttribute('data-ref') === selectedRef,
          );
        return index === -1 ? 0 : index;
      }

      return 0;
    }

    let selectedIndex = getSelectedIndex();

    function moveSelected(deltaIndex: number) {
      const currentIndex = selectedIndex;

      selectedIndex = Math.min(
        Math.max(0, selectedIndex + deltaIndex),
        cells.size() - 1,
      );

      const cellNodes = cells.nodes();

      const currentRect = cellNodes.at(currentIndex)?.querySelector('rect');
      const nextCell = cellNodes.at(selectedIndex);
      const nextRect = nextCell?.querySelector('rect');

      // Remove selection stroke
      if (currentRect) {
        currentRect.removeAttribute('stroke');
        currentRect.removeAttribute('stroke-width');
        currentRect.removeAttribute('stroke-dasharray');
      }

      const nextDatum = cells.data()[selectedIndex];

      // Add selection stroke
      if (nextRect && nextDatum) {
        nextRect.setAttribute('stroke', highlight(nextDatum.value));
        nextRect.setAttribute('stroke-width', '1.5');
        nextRect.setAttribute('stroke-dasharray', '5,5');
      }

      svg.attr('aria-activedescendant', nextCell?.id ?? null);

      if (nextCell) {
        // nextCell.scrollIntoView();
      }

      if (nextDatum) {
        highlightDatum(nextDatum);
      }
    }

    // Generic keyboard handling
    svg
      .on('focus', this.onFocused)
      .on('blur', this.onBlur)
      .on('keydown', (event: KeyboardEvent) => {
        switch (event.key) {
          case 'ArrowUp': {
            event.preventDefault();
            moveSelected(-1);
            break;
          }
          case 'ArrowDown': {
            event.preventDefault();
            moveSelected(1);
            break;
          }
          case 'ArrowLeft': {
            event.preventDefault();
            moveSelected(-7);
            break;
          }
          case 'ArrowRight': {
            event.preventDefault();
            moveSelected(7);
            break;
          }
          case 'Enter': {
            const currentDatum = cells.data()[selectedIndex];
            selectDatum(currentDatum);
            break;
          }
          case 'Escape': {
            selectDatum(undefined);
          }
        }
      });

    this.element.append(svg.node()!);

    if (this.hasTableTarget) {
      this.tableTarget.classList.add('sr-only!');
    }

    if (this.hasD3Target) {
      this.d3Target.setAttribute('tabindex', '0');
      this.d3Target.classList.add(
        'outline-hidden',
        'focus-visible:ring-2',
        'ring-theme-primary-dark',
        'dark:ring-theme-primary-light',
        'rounded-md',
      );

      this.d3Target.addEventListener('focus', this.onFocused);
      this.d3Target.addEventListener('blur', this.onBlur);
      this.d3Target.setAttribute('role', 'group');
      this.d3Target.removeAttribute('aria-hidden');
    }
  }

  disconnect(): void {
    if (this.hasTableTarget) {
      this.tableTarget.classList.remove('sr-only!');
    }

    if (this.hasD3Target) {
      this.d3Target.remove();
    }
  }

  private onFocused(): void {
    this.focused = true;

    // if (!this.hasD3Target) {
    //   return;
    // }

    /*this.d3Target.addEventListener('click', this.onClick);
    document.addEventListener('keydown', this.onKeyPress);*/
  }

  private onBlur(): void {
    this.focused = false;

    if (!this.hasD3Target) {
      return;
    }

    /*this.d3Target.removeEventListener('click', this.onClick);
    document.removeEventListener('keydown', this.onKeyPress);*/
  }

  private onClick(event: Event) {
    event.preventDefault();
  }
}
